import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Release {
  id: string
  version: string
  title: string
  description: string
  type: string
  changes: { category: string; description: string }[]
  release_date: string
  published: boolean
}

export default function ChangelogPage() {
  const [releases, setReleases] = useState<Release[]>([])
  const [statistics, setStatistics] = useState<{total_releases: number; latest_version: string; types: Record<string, number>} | null>(null)
  const [selectedType, setSelectedType] = useState<string>('all')

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [relRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/changelog/releases'),
      fetch(API_BASE + '/changelog/statistics')
    ])
    if (relRes.ok) setReleases((await relRes.json()).releases || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'major': return 'bg-red-100 text-red-800 border-red-200'
      case 'minor': return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'patch': return 'bg-green-100 text-green-800 border-green-200'
      default: return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'feature': return '✨'
      case 'bugfix': return '🐛'
      case 'improvement': return '🚀'
      case 'security': return '🔒'
      case 'deprecation': return '⚠️'
      default: return '📝'
    }
  }

  const filteredReleases = selectedType === 'all'
    ? releases
    : releases.filter(r => r.type === selectedType)

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Changelog & Release Notes</h1>
          {statistics && (
            <div className="text-sm text-gray-500">
              Latest: <span className="font-semibold text-blue-600">v{statistics.latest_version}</span>
            </div>
          )}
        </div>

        {statistics && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Releases</div>
              <div className="text-2xl font-bold">{statistics.total_releases}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 cursor-pointer hover:ring-2 ring-red-200" onClick={() => setSelectedType('major')}>
              <div className="text-sm text-gray-500">Major</div>
              <div className="text-2xl font-bold text-red-600">{statistics.types?.major || 0}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 cursor-pointer hover:ring-2 ring-blue-200" onClick={() => setSelectedType('minor')}>
              <div className="text-sm text-gray-500">Minor</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.types?.minor || 0}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 cursor-pointer hover:ring-2 ring-green-200" onClick={() => setSelectedType('patch')}>
              <div className="text-sm text-gray-500">Patch</div>
              <div className="text-2xl font-bold text-green-600">{statistics.types?.patch || 0}</div>
            </div>
          </div>
        )}

        <div className="flex gap-2 mb-6">
          {['all', 'major', 'minor', 'patch'].map(type => (
            <button
              key={type}
              onClick={() => setSelectedType(type)}
              className={`px-4 py-2 rounded-lg capitalize ${
                selectedType === type
                  ? 'bg-blue-600 text-white'
                  : 'bg-white border hover:bg-gray-50'
              }`}
            >
              {type}
            </button>
          ))}
        </div>

        <div className="space-y-6">
          {filteredReleases.map(release => (
            <div key={release.id} className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getTypeColor(release.type)}`}>
                      v{release.version}
                    </span>
                    <h2 className="text-lg font-semibold">{release.title}</h2>
                  </div>
                  <div className="text-sm text-gray-500">
                    {new Date(release.release_date).toLocaleDateString()}
                  </div>
                </div>
              </div>
              <div className="px-6 py-4">
                <p className="text-gray-600 mb-4">{release.description}</p>
                <div className="space-y-2">
                  {release.changes.map((change, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <span>{getCategoryIcon(change.category)}</span>
                      <span className="text-sm">{change.description}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  )
}
